/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { Heading } from '@msdyn365-commerce-modules/utilities';
import { ICookieSetOptions, RichTextComponent } from '@msdyn365-commerce/core';
import { inputGroup, mailingData, MailingListInputGroup } from '../../components/input-group-mailing-list';
import { createMailingListInput, mailingListAction } from '../../dataActions/mailing-list-signup';
import { popupModalCookie } from '../mailing-list-popup-modal/mailing-list-popup-modal';
import { IMailingListFormProps } from './mailing-list-form.props.autogenerated';

export interface IMailingListFormViewProps extends IMailingListFormProps<{}> {
    displayInPopup: boolean;
}

export const MAILING_FORM_COOKIE: popupModalCookie = {
    name: 'mailingListCookie',
    age: 2592000
};

interface IMailingListFormViewState {
    isSubmittedSuccessfully: boolean;
    error: string;
}

/**
 *
 * MailingListForm component
 * @extends {React.PureComponent<IMailingListFormViewProps>}
 */
class MailingListForm extends React.PureComponent<IMailingListFormViewProps, IMailingListFormViewState> {

    constructor(props: IMailingListFormViewProps) {
        // tslint:disable-next-line:no-any
        (props.config as any).displayInPopup = true;
        super(props);

        this.state = { isSubmittedSuccessfully: false, error: '' };
        this.onFinished = this.onFinished.bind(this);
    }

    public render(): JSX.Element | null {
        const {
            isSubmittedSuccessfully
        } = this.state;
        if (isSubmittedSuccessfully) {
            return this._signupSuccessJSX;
        } else {
            return this._mailingListJSX;
        }
    }

    private get _signupSuccessJSX(): JSX.Element {
        const {
            layout,
            className,
            confirmationText
        } = this.props.config;
        return (
            <div className={`mailing-list mailing-list-${layout} ${className}`}>
                <div className='mailing-list-form-list' tabIndex={0}>
                    <RichTextComponent
                        className='mailing-list-success-message'
                        text={confirmationText as string}
                        aria-live='polite'
                    />
                </div>
            </div>
        );
    }

    private get _mailingListJSX(): JSX.Element {
        const groups = this.constructMailingListGroupsFromResources();
        const {
            layout,
            title, subtitle, description,
            className
        } = this.props.config;

        const { error } = this.state;
        let submitButtonText = this.props.resources.mailingListForm__submitButton;
        if (this.props.config.submitButtonText) {
            submitButtonText = this.props.config.submitButtonText;
        }
        return (
            <div className={`mailing-list mailing-list-${layout} ${className}`}>

                {(title || description) &&
                    <div className='mailing-list-form-marketing'>
                        {title && <Heading {...title} className='mailing-list-form-marketing-title' />}
                        {description && <RichTextComponent text={description} className='mailing-list-form-marketing-description' />}
                    </div>
                }

                <div className='mailing-list-form-list'>
                    {subtitle && <Heading {...subtitle} className='mailing-list-form-list-subtitle' />}
                    <MailingListInputGroup
                        submitText={submitButtonText}
                        email={groups.email}
                        name={groups.name}
                        zip={groups.zip}
                        onFinish={this.onFinished}
                    />
                    {error && <div className='text-input-validation-error' role='alert' aria-live={'assertive'}>{error}</div>}
                </div>
            </div>
        );
    }

    public onFinished(dataSet: mailingData): void {
        const input = createMailingListInput(
            {
                email: dataSet.email,
                name: dataSet.name,
                zipCode: dataSet.zip
            },
            this.props.context
        );

        mailingListAction(input, this.props.context.actionContext)
            // tslint:disable-next-line:no-any
            .then((requestFullfilled: any) => {
                if (requestFullfilled.IsSaved) {
                    this.props.context.request.cookies.set(
                        MAILING_FORM_COOKIE.name,
                        true,
                        { path: '/', maxAge: MAILING_FORM_COOKIE.age * 12 * 10, isEssential: true } as ICookieSetOptions
                    );
                    this.setState({ isSubmittedSuccessfully: true });
                } else {
                    // assumes all errors are related to an email already existing, error message is not very helpful
                    this.setState({ error: this.props.resources.mailingListForm__emailServerInvalid });
                }
            })
            .catch(e => { throw new Error(JSON.stringify(e, null, 4)); });
    }

    public constructMailingListGroupsFromResources(): { [groupName: string]: inputGroup } {
        const {
            mailingListForm__emailLabel, mailingListForm__emailValidation,
            mailingListForm__nameLabel, mailingListForm__nameValidation,
            mailingListForm__zipLabel, mailingListForm__zipValidation
        } = this.props.resources;
        const {  email, enablePlaceholders, name, zip  } = this.props.config.placeholders || {};
        return {
            email: {
                labelText: mailingListForm__emailLabel,
                validationText: mailingListForm__emailValidation,
                placeholder: enablePlaceholders ? email : undefined
            },
            name: {
                labelText: mailingListForm__nameLabel,
                validationText: mailingListForm__nameValidation,
                placeholder: enablePlaceholders ? name : undefined
            },
            zip: {
                labelText: mailingListForm__zipLabel,
                validationText: mailingListForm__zipValidation,
                placeholder: enablePlaceholders ? zip : undefined
            }
        };
    }
}

export default MailingListForm;
